import React from "react";
import { Outlet, Link, useNavigate, useSearchParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  Award,
  BarChart3,
  BookOpen,
  GraduationCap,
  LineChart,
  LogOut,
  MapPin,
  School,
  Target,
} from "lucide-react";
import { ScopeProvider } from "@/context/ScopeContext";
import { getProgramConfig, getProgramFromPath } from "@/components/programDashboards/programDashboardConfig";
import "../pages/SqafDashboard.css";

const PROGRAM_ICONS = {
  pgi: Award,
  nas: BookOpen,
  nipun: Target,
  ger: LineChart,
  pat: GraduationCap,
};

const TAB_ICONS = {
  executive: BarChart3,
  blocks: MapPin,
  domains: Target,
  insights: GraduationCap,
};

const StandaloneProgramLayout = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const programId = getProgramFromPath(location.pathname);
  const config = getProgramConfig(programId);
  const activeTab = searchParams.get("tab") || "executive";
  const BrandIcon = PROGRAM_ICONS[programId] || Award;

  const handleLogout = () => {
    if (onLogout) onLogout();
    navigate("/login");
  };

  const goToTab = (tabId) => {
    if (!config) return;
    const next = new URLSearchParams(searchParams);
    if (tabId === "executive") next.delete("tab");
    else next.set("tab", tabId);
    navigate({ pathname: `/${config.slug}`, search: next.toString() ? `?${next.toString()}` : "" });
  };

  if (!config) {
    return (
      <div className="sqaf-dashboard-root">
        <main className="main" style={{ marginLeft: 0, padding: 40 }}>
          <p>Program dashboard not found.</p>
          <Link to="/">Back to MH Education</Link>
        </main>
      </div>
    );
  }

  return (
    <ScopeProvider>
      <div className="sqaf-dashboard-root program-dashboard-root" style={{ "--program-accent": config.accent }}>
        <div className="app-shell">
          <aside className="sq-sidebar" aria-label={`${config.title} sidebar`}>
            <div className="sq-sidebar-brand">
              <div className="sq-sidebar-brand-main">
                <span className="sq-brand-icon" aria-hidden="true">
                  <BrandIcon className="w-[30px] h-[30px]" strokeWidth={1.75} />
                </span>
                <span className="sq-brand-copy">
                  <strong>{config.title.replace(" Dashboard", "")}</strong>
                  <span>{config.subtitle}</span>
                </span>
              </div>
            </div>

            <div className="sq-sidebar-scroll">
              <section className="sq-sidebar-section">
                <div className="sq-section-title">Sections</div>
                <nav className="sq-nav">
                  {config.sidebarItems.map(({ id, label }) => {
                    const Icon = TAB_ICONS[id] || BarChart3;
                    return (
                      <button
                        key={id}
                        type="button"
                        className={`sq-nav-item${activeTab === id ? " active" : ""}`}
                        onClick={() => goToTab(id)}
                        data-testid={`${programId}-nav-${id}`}
                      >
                        <Icon strokeWidth={2} />
                        {label}
                      </button>
                    );
                  })}
                </nav>
              </section>
            </div>

            <div className="sq-sidebar-footer">
              <div className="sync-pill">
                <b>District Review Mode</b>
                {config.footerNote}
              </div>
            </div>
          </aside>

          <main className="main">
            <header className="topbar">
              <div className="topbar-inner">
                <div className="top-title">
                  <Link className="back-link" to="/">
                    <ArrowLeft className="svg-icon" strokeWidth={2} />
                    Back to MH Education
                  </Link>
                  <div className="title-mini">
                    <span className="mini-icon">
                      <BrandIcon className="svg-icon" strokeWidth={2} />
                    </span>
                    <div>
                      <strong>{config.title}</strong>
                      <span>{config.subtitle}</span>
                    </div>
                  </div>
                </div>
                <div className="top-actions">
                  <span className="user-name">{user?.full_name || "System Administrator"}</span>
                  <button type="button" className="icon-button" title="Sign out" onClick={handleLogout}>
                    <LogOut className="svg-icon" strokeWidth={2} />
                  </button>
                </div>
              </div>
            </header>
            <Outlet context={{ programId, config }} />
          </main>
        </div>
      </div>
    </ScopeProvider>
  );
};

export default StandaloneProgramLayout;
